import {Component, computed, inject, OnInit, input, output} from '@angular/core';
import {Button} from 'primeng/button';
import {translate, TranslocoDirective} from '@jsverse/transloco';
import {MenuItem, MessageService} from 'primeng/api';
import {Menu} from 'primeng/menu';
import {
  ProjectDetailResponse,
  ProjectUserDetailResponse,
  ProjectUserStatusType,
} from '@frontend/shared-api';
import {
  CardLevel,
  GenericButtonCardComponent,
} from '../../../../../../shared/components/card/button-card/generic-button-card.component';
import {ProgressBarComponent} from '../../../../../../shared/components/progress-bar/progress-bar.component';
import {RoleCheckService} from '../../../../../../shared/services/role-check.service';
import {
  ProjectRoleAccessDirective,
  ProjectRoleConfig,
} from '../../../../../../shared/directives/project-role-access.directive';
import {ProjectRoleConfigService} from '../../../../../../shared/services/project-role-config.service';
import {AppDatePipe} from '@frontend/shared';
import {ConfirmationDialogService} from '../../../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'app-project-card',
  imports: [
    Button,
    TranslocoDirective,
    Menu,
    GenericButtonCardComponent,
    ProgressBarComponent,
    ProjectRoleAccessDirective,
    AppDatePipe,
  ],
  templateUrl: './project-card.component.html',
  providers: [MessageService],
})
export class ProjectCardComponent implements OnInit {
  readonly project = input.required<ProjectDetailResponse>();
  readonly projectUserDetail = input<ProjectUserDetailResponse>();
  readonly chaptersCompleted = input(0);
  readonly totalChapters = input(12);

  readonly goToProjectEvent = output<void>();
  readonly editProjectEvent = output<number>();
  readonly archiveProjectEvent = output<number>();
  readonly deleteProjectEvent = output<number>();
  readonly copyProjectEvent = output<number>();
  readonly invitationResponseEvent = output<{
    projectId: number;
    accept: boolean;
  }>();

  menuItems: MenuItem[] = [];
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly roleCheckService = inject(RoleCheckService);
  private readonly roleConfigService = inject(ProjectRoleConfigService);

  ngOnInit() {
    this.menuItems = [
      {
        label: translate('project.card.menu.edit'),
        icon: 'pi pi-pencil',
        command: () => this.editProjectEvent.emit(this.project().id),
        visible: this.project().isEditable && !this.project().archivedAt,
      },
      {
        label: translate('project.card.menu.copy'),
        icon: 'pi pi-copy',
        command: () => this.copyProjectEvent.emit(this.project().id),
        visible: this.roleCheckService.hasCustomerAdminRole(),
      },
      {
        label: translate('project.card.menu.archive'),
        icon: 'pi pi-folder',
        command: () => this.confirmArchive(),
        disabled: !!this.project().archivedAt,
        visible: !this.project().archivedAt,
      },
      {
        label: translate('project.card.menu.delete'),
        icon: 'pi pi-trash',
        command: () => this.confirmDelete(),
        disabled: !this.project().isDeletable,
        visible: !this.project().archivedAt,
      },
    ];
  }

  get hasVisibleMenuItems(): boolean {
    return this.menuItems.some((item) => item.visible !== false);
  }

  confirmArchive() {
    this.confirmDialogService.confirm({
      headerKey: 'project.card.confirmArchive.header',
      messageKey: 'project.card.confirmArchive.message',
      accept: () => this.archiveProjectEvent.emit(this.project().id),
    });
  }

  confirmDelete() {
    this.confirmDialogService.confirm({
      headerKey: 'project.card.confirmDelete.header',
      messageKey: 'project.card.confirmDelete.message',
      accept: () => this.deleteProjectEvent.emit(this.project().id),
    });
  }

  confirmAccept() {
    this.confirmDialogService.confirm({
      headerKey: 'project.card.confirmAccept.header',
      messageKey: 'project.card.confirmAccept.message',
      accept: () => this.invitationResponseEvent.emit({projectId: this.project().id, accept: true}),
    });
  }

  confirmReject() {
    this.confirmDialogService.confirm({
      headerKey: 'project.card.confirmReject.header',
      messageKey: 'project.card.confirmReject.message',
      accept: () =>
        this.invitationResponseEvent.emit({projectId: this.project().id, accept: false}),
    });
  }

  onGoToProject() {
    this.goToProjectEvent.emit();
  }

  get userStatus(): ProjectUserStatusType | undefined {
    return this.projectUserDetail()?.status;
  }

  get isInvited(): boolean {
    return this.userStatus === ProjectUserStatusType.INVITED;
  }

  get isActiveOrTenantAdmin(): boolean {
    return (
      this.userStatus === ProjectUserStatusType.ACTIVE ||
      this.roleCheckService.hasCustomerAdminRole()
    );
  }

  get isTenantAdmin(): boolean {
    return (
      this.userStatus === ProjectUserStatusType.ACTIVE &&
      this.roleCheckService.hasCustomerAdminRole()
    );
  }

  get isRejected(): boolean {
    return this.userStatus === ProjectUserStatusType.REJECTED;
  }

  protected readonly projectManagementConfig = computed((): ProjectRoleConfig => {
    return this.roleConfigService.createProjectManagerWithTenantAdminConfig(
      this.project().id,
      this.projectUserDetail(),
    );
  });

  protected readonly CardLevel = CardLevel;
}
